/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import { forwardRef } from "react";
import { Typography, TypographyProps } from "@mui/material";
import { motion, MotionValue, useTransform } from "motion/react";
import { useUserPreference } from "@/src/hooks/useUserPreference.ts";

const SpanTypography = forwardRef<HTMLSpanElement, Omit<TypographyProps, "component">>((props, ref) => <Typography component="span" ref={ref} {...props} />);

const MotionTypography = motion.create(SpanTypography);

export type OpacityFillingTextLetterProps = {
  letter: string;
  percentInText: number;
  scrollYProgress: MotionValue<number>;
  fadeOutPercentage?: number;
  disabledOpacity?: number;
  isInView?: boolean;
} & Pick<TypographyProps, "variant">;

export const OpacityFillingTextLetter = ({
  letter,
  percentInText,
  scrollYProgress,
  isInView,
  fadeOutPercentage = 0.025,
  disabledOpacity = 0.125,
  variant = "h3",
}: OpacityFillingTextLetterProps) => {
  const { showAnimation } = useUserPreference();

  const opacity = useTransform(scrollYProgress, (scrollYProgress) => {
    if (!showAnimation) {
      return 1;
    }

    if (!isInView) {
      return disabledOpacity;
    }

    if (scrollYProgress > 0 && percentInText < scrollYProgress + fadeOutPercentage) {
      if (percentInText > scrollYProgress) {
        return 1 - ((percentInText - scrollYProgress) / fadeOutPercentage) * (1 - disabledOpacity);
      }

      return 1;
    }
    return disabledOpacity;
  });

  return (
    <MotionTypography
      variant={variant}
      style={{
        opacity: opacity,
        transitionDuration: "100ms",
        transitionTimingFunction: "cubic-bezier(0, 0, 0.24, 0.97)",
      }}
    >
      {letter}
    </MotionTypography>
  );
};
